import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { HubConnectionState } from "@microsoft/signalr";
import { Mic, MicOff, Phone, PhoneOff, Video, VideoOff, X } from "lucide-react";
import {
    playRingtone,
    type Ringtone,
    useMeetingMesh,
    type MeetingMeshSignaling,
    type MeshHandlers
} from "@athavita/ui-theme";
import { connectBaseUrl, normalizeUserId } from "./connectClient";
import { useAccessToken } from "@/auth/useAccessToken";
import { MeetingsEvents, useMeetingsHub } from "./useMeetingsHub";
import { cn } from "@/lib/utils";

/**
 * Gmail-style call experience embedded inside Mail. No tab navigation —
 * the same dock that hosts chat windows hosts the active call panel.
 *
 * State machine:
 *   - idle:      nothing rendered
 *   - outgoing:  small "Calling …" card with Cancel
 *   - incoming:  toast with Accept / Decline + ringtone (auto-shows
 *                when the BFF emits a `meeting.invited` event)
 *   - active:    compact tile-grid panel with mute/cam/end controls
 *
 * Communication with the rest of mail-web is via custom events:
 *   - `athavita:startCall`  detail: { peerEmail, peerDisplayName, kind }
 *   - `athavita:endCall`
 */
export const START_CALL_EVENT = "athavita:startCall";
export interface StartCallDetail {
    peerEmail: string;
    peerDisplayName: string;
    kind: "audio" | "video";
}

interface OutgoingState {
    meetingId: string;
    subject: string;
    peerLabel: string;
    kind: "audio" | "video";
}

interface IncomingState {
    meetingId: string;
    subject: string;
    hostDisplayName: string;
    kind: "audio" | "video";
}

interface ActiveState {
    meetingId: string;
    subject: string;
    kind: "audio" | "video";
}

export function CallDock() {
    const token = useAccessToken();
    const auth = useAuth();
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const myUserId = normalizeUserId(auth.user?.profile.sub);

    const [outgoing, setOutgoing] = useState<OutgoingState | null>(null);
    const [incoming, setIncoming] = useState<IncomingState | null>(null);
    const [active, setActive] = useState<ActiveState | null>(null);
    const ringRef = useRef<Ringtone | null>(null);

    // Subscribe to the Connect SignalR hub for incoming meetings. The BFF
    // emits `meeting.invited` to the invitee's email-keyed group when a
    // meeting is created, and `meeting.started` when it goes Live. We
    // pop the toast on either (Live = "answer now", invited-future = the
    // user can ignore until later, but for 1:1 audio/video calls the
    // server marks them isCallNow so we ring immediately).
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        let stop: (() => void) | null = null;
        (async () => {
            const { HubConnectionBuilder, LogLevel } = await import("@microsoft/signalr");
            const connection = new HubConnectionBuilder()
                .withUrl(`${connectBaseUrl}/hubs/connect`, { accessTokenFactory: () => token })
                .withAutomaticReconnect()
                .configureLogging(LogLevel.Warning)
                .build();
            connection.on("meeting.invited", (payload: {
                meetingId: string;
                subject: string;
                hostDisplayName: string;
                hostUserId?: string;
                isCallNow?: boolean;
                kind?: "audio" | "video";
            }) => {
                if (payload.hostUserId && normalizeUserId(payload.hostUserId) === myUserId) return;
                if (!payload.isCallNow) return;
                const kind = payload.kind ?? (payload.subject.toLowerCase().includes("video") ? "video" : "audio");
                openIncoming({ meetingId: payload.meetingId, subject: payload.subject, hostDisplayName: payload.hostDisplayName, kind });
            });
            connection.on("meeting.started", (payload: {
                meetingId: string;
                subject: string;
                hostUserId?: string;
                kind?: "audio" | "video";
            }) => {
                if (payload.hostUserId && normalizeUserId(payload.hostUserId) === myUserId) return;
                const kind = payload.kind ?? (payload.subject.toLowerCase().includes("video") ? "video" : "audio");
                openIncoming({ meetingId: payload.meetingId, subject: payload.subject, hostDisplayName: "Caller", kind });
            });
            connection.on("meeting.ended", (payload: { meetingId: string }) => {
                // Caller hung up — clear the ringing toast or the
                // silently-waiting outgoing card for this meeting.
                setIncoming((cur) => (cur && cur.meetingId === payload.meetingId ? null : cur));
                setOutgoing((cur) => (cur && cur.meetingId === payload.meetingId ? null : cur));
                ringRef.current?.stop();
                ringRef.current = null;
            });
            try {
                await connection.start();
                if (cancelled) { await connection.stop(); return; }
                stop = () => void connection.stop();
            } catch (err) { console.warn("Call hub start failed", err); }
        })();
        return () => {
            cancelled = true;
            if (stop) stop();
            ringRef.current?.stop();
            ringRef.current = null;
        };
    }, [token, myUserId]);

    const openIncoming = useCallback((s: IncomingState) => {
        setIncoming((prev) => prev ?? s);
        if (!ringRef.current) {
            try { ringRef.current = playRingtone(); } catch { /* autoplay blocked */ }
        }
    }, []);

    const dismissIncoming = useCallback(() => {
        setIncoming(null);
        ringRef.current?.stop();
        ringRef.current = null;
    }, []);

    // External outgoing-call trigger from the chat window's Phone/Video
    // buttons. Creates the meeting + starts it + opens our active panel.
    useEffect(() => {
        const handler = async (e: Event) => {
            const detail = (e as CustomEvent<StartCallDetail>).detail;
            if (!detail || !token) return;
            try {
                const subject = `${detail.kind === "video" ? "Video" : "Audio"} call with ${detail.peerDisplayName}`;
                const res = await fetch(`${connectBaseUrl}/api/meetings`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                    body: JSON.stringify({
                        subject,
                        scheduledStart: new Date().toISOString(),
                        invitees: [detail.peerEmail]
                    })
                });
                if (!res.ok) throw new Error(`create ${res.status}`);
                const meeting = (await res.json()) as { id: string };
                await fetch(`${connectBaseUrl}/api/meetings/${meeting.id}/start`, {
                    method: "POST",
                    headers: { Authorization: `Bearer ${token}` }
                });
                setOutgoing({
                    meetingId: meeting.id,
                    subject,
                    peerLabel: detail.peerDisplayName,
                    kind: detail.kind
                });
            } catch (err) {
                console.warn("Start call failed", err);
            }
        };
        window.addEventListener(START_CALL_EVENT, handler as EventListener);
        return () => window.removeEventListener(START_CALL_EVENT, handler as EventListener);
    }, [token]);

    const acceptIncoming = useCallback(async () => {
        if (!incoming || !token) return;
        try {
            await fetch(`${connectBaseUrl}/api/meetings/${incoming.meetingId}/join`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
            setActive({ meetingId: incoming.meetingId, subject: incoming.subject, kind: incoming.kind });
            dismissIncoming();
        } catch (err) {
            console.warn("Accept call failed", err);
        }
    }, [incoming, token, dismissIncoming]);

    const cancelOutgoing = useCallback(async () => {
        if (!outgoing || !token) { setOutgoing(null); return; }
        try {
            await fetch(`${connectBaseUrl}/api/meetings/${outgoing.meetingId}/end`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` }
            });
        } catch { /* best effort */ }
        setOutgoing(null);
    }, [outgoing, token]);

    // When the outgoing call is answered (mesh sees a peer arrive), promote
    // it to active. Until then, just show the "Calling…" card.
    const promoteOutgoing = useCallback(() => {
        if (!outgoing) return;
        setActive({ meetingId: outgoing.meetingId, subject: outgoing.subject, kind: outgoing.kind });
        setOutgoing(null);
    }, [outgoing]);

    return (
        <>
            {outgoing ? (
                <OutgoingCard
                    state={outgoing}
                    onCancel={() => void cancelOutgoing()}
                    onPeerJoined={promoteOutgoing}
                />
            ) : null}
            {incoming ? (
                <IncomingToast
                    state={incoming}
                    onAccept={() => void acceptIncoming()}
                    onDecline={dismissIncoming}
                />
            ) : null}
            {active ? (
                <ActivePanel
                    state={active}
                    selfEmail={myEmail}
                    onLeave={() => setActive(null)}
                />
            ) : null}
        </>
    );
}

function OutgoingCard({
    state, onCancel, onPeerJoined
}: {
    state: OutgoingState;
    onCancel: () => void;
    onPeerJoined: () => void;
}) {
    // Outgoing card joins the meeting silently in the background so the
    // moment the peer accepts on their side, the mesh sees them and we
    // can flip to the active panel automatically.
    const signaling = useMakeSignaling();
    const mesh = useMeetingMesh({
        meetingId: state.meetingId,
        signaling,
        constraints: state.kind === "video" ? { audio: true, video: true } : { audio: true, video: false },
        onPeerJoined
    });

    // Auto-hang-up after 30 seconds if nobody answered. Mirrors the
    // standard mobile/desktop behaviour — keeps the caller from staring
    // at a forever-spinning "Calling…" card when the peer is AFK.
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const id = window.setInterval(() => setElapsed((e) => e + 1), 1000);
        return () => window.clearInterval(id);
    }, []);
    const RING_TIMEOUT_SECS = 30;
    useEffect(() => {
        if (elapsed >= RING_TIMEOUT_SECS) {
            onCancel();
        }
    }, [elapsed, onCancel]);

    return (
        <div className="pointer-events-auto fixed bottom-3 right-4 z-40 mb-3 flex w-72 items-center gap-3 rounded-lg border bg-card p-3 shadow-2xl">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                {state.kind === "video" ? <Video className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">Calling {state.peerLabel}…</div>
                <div className="truncate text-[11px] text-muted-foreground">
                    {mesh.mediaError
                        ? mesh.mediaError
                        : `${state.kind === "video" ? "Video call" : "Audio call"} · ${RING_TIMEOUT_SECS - elapsed}s`}
                </div>
            </div>
            <button
                type="button"
                onClick={onCancel}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-destructive text-destructive-foreground hover:opacity-90"
                aria-label="Cancel call"
            >
                <PhoneOff className="h-4 w-4" />
            </button>
        </div>
    );
}

function IncomingToast({
    state, onAccept, onDecline
}: {
    state: IncomingState;
    onAccept: () => void;
    onDecline: () => void;
}) {
    // Auto-dismiss after 45s. Gives a comfortable margin over the 30s
    // caller-side ring timeout so we never dismiss before the caller's
    // /end fires, which otherwise would leave the caller hanging while
    // the receiver's toast was already gone.
    useEffect(() => {
        const id = window.setTimeout(() => onDecline(), 45_000);
        return () => window.clearTimeout(id);
    }, [onDecline]);

    return (
        <div
            role="dialog"
            aria-label="Incoming call"
            aria-live="assertive"
            className="pointer-events-auto fixed bottom-3 right-4 z-50 flex w-80 flex-col gap-3 rounded-lg border bg-card p-4 shadow-2xl"
        >
            <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600">
                    <Phone className="h-5 w-5 animate-pulse" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Incoming call
                    </div>
                    <div className="truncate text-sm font-semibold">{state.hostDisplayName}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{state.subject}</div>
                </div>
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={onAccept}
                    className="flex-1 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                    Accept
                </button>
                <button
                    type="button"
                    onClick={onDecline}
                    className="rounded-md border bg-card px-3 py-1.5 text-sm hover:bg-accent"
                >
                    Decline
                </button>
            </div>
        </div>
    );
}

function ActivePanel({
    state, selfEmail, onLeave
}: {
    state: ActiveState;
    selfEmail: string;
    onLeave: () => void;
}) {
    const signaling = useMakeSignaling();
    const mesh = useMeetingMesh({
        meetingId: state.meetingId,
        signaling,
        constraints: state.kind === "video" ? { audio: true, video: true } : { audio: true, video: false }
    });
    const peers = Object.values(mesh.peers);
    const localRef = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (localRef.current && mesh.localStream) localRef.current.srcObject = mesh.localStream;
    }, [mesh.localStream]);

    const leave = () => {
        mesh.teardown();
        onLeave();
    };

    return (
        <div className="pointer-events-auto fixed bottom-3 right-4 z-40 flex w-[22rem] flex-col gap-2 rounded-lg border bg-card p-3 shadow-2xl">
            <header className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/15 text-primary">
                    {state.kind === "video" ? <Video className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold">{state.subject}</div>
                    <div className="text-[10px] text-muted-foreground">
                        {peers.length === 0 ? "Just you" : `${peers.length + 1} on call`}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={leave}
                    className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-accent"
                    aria-label="Close call view"
                >
                    <X className="h-3.5 w-3.5" />
                </button>
            </header>

            {/* Video tiles — local self in the bottom-right corner like Gmail Meet, peer fills the panel */}
            <div className="relative h-44 overflow-hidden rounded-md bg-zinc-900">
                {peers[0] ? (
                    <RemoteTile peer={peers[0]} />
                ) : (
                    <div className="flex h-full items-center justify-center text-xs text-white/60">
                        Waiting for {state.subject.split("with").pop()?.trim() ?? "peer"}…
                    </div>
                )}
                {state.kind === "video" ? (
                    <div className="absolute bottom-1 right-1 h-16 w-24 overflow-hidden rounded border border-white/30 bg-black">
                        <video
                            ref={localRef}
                            autoPlay
                            playsInline
                            muted
                            className={cn("h-full w-full object-cover", !mesh.cameraOn && "hidden")}
                        />
                        {!mesh.cameraOn ? (
                            <div className="flex h-full items-center justify-center text-[10px] text-white/70">
                                Cam off
                            </div>
                        ) : null}
                    </div>
                ) : null}
            </div>

            <footer className="flex items-center justify-center gap-2">
                <ToolButton onClick={mesh.toggleMic} active={!mesh.micOn} label={mesh.micOn ? "Mute" : "Unmute"}>
                    {mesh.micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                </ToolButton>
                {state.kind === "video" ? (
                    <ToolButton onClick={mesh.toggleCamera} active={!mesh.cameraOn} label={mesh.cameraOn ? "Camera off" : "Camera on"}>
                        {mesh.cameraOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                    </ToolButton>
                ) : null}
                <button
                    type="button"
                    onClick={leave}
                    className="flex h-9 items-center gap-1 rounded-md bg-destructive px-3 text-xs font-semibold text-destructive-foreground"
                    title="End call"
                    aria-label="End call"
                >
                    <PhoneOff className="h-3.5 w-3.5" /> End
                </button>
            </footer>
            {/* selfEmail kept around so future "you on this device" subtitles can render. */}
            <span className="sr-only">{selfEmail}</span>
        </div>
    );
}

function RemoteTile({ peer }: { peer: { stream: MediaStream | null; displayName: string; email: string } }) {
    const ref = useRef<HTMLVideoElement>(null);
    useEffect(() => {
        if (ref.current && peer.stream) ref.current.srcObject = peer.stream;
    }, [peer.stream]);
    return peer.stream ? (
        <video ref={ref} autoPlay playsInline className="h-full w-full object-cover" />
    ) : (
        <div className="flex h-full items-center justify-center text-xs text-white/60">
            Connecting to {peer.displayName || peer.email}…
        </div>
    );
}

function ToolButton({
    children, onClick, active, label
}: {
    children: React.ReactNode;
    onClick: () => void;
    active?: boolean;
    label: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className={cn(
                "flex h-9 w-9 items-center justify-center rounded-md",
                active
                    ? "bg-destructive text-destructive-foreground"
                    : "bg-muted hover:bg-accent"
            )}
        >
            {children}
        </button>
    );
}

/**
 * Build a `MeetingMeshSignaling` adapter on top of mail-web's slim
 * useMeetingsHub. The mesh hook is UI-agnostic; this function plugs in
 * the concrete SignalR send/on calls.
 */
function useMakeSignaling(): MeetingMeshSignaling {
    const handlersRef = useRef<MeshHandlers>({});

    const hub = useMeetingsHub({
        [MeetingsEvents.Participants]: (payload) => {
            const p = payload as { peers: Array<{ connectionId: string; userId: string; email: string; displayName: string }> };
            handlersRef.current.onParticipants?.(p.peers);
        },
        [MeetingsEvents.UserJoined]: (payload) => {
            handlersRef.current.onUserJoined?.(payload as { connectionId: string; userId: string; email: string; displayName: string });
        },
        [MeetingsEvents.UserLeft]: (payload) => {
            const p = payload as { connectionId: string };
            handlersRef.current.onUserLeft?.(p.connectionId);
        },
        [MeetingsEvents.ReceiveOffer]: (payload) => {
            const p = payload as { fromConnectionId: string; offer: RTCSessionDescriptionInit };
            handlersRef.current.onOffer?.(p.fromConnectionId, p.offer);
        },
        [MeetingsEvents.ReceiveAnswer]: (payload) => {
            const p = payload as { fromConnectionId: string; answer: RTCSessionDescriptionInit };
            handlersRef.current.onAnswer?.(p.fromConnectionId, p.answer);
        },
        [MeetingsEvents.ReceiveIceCandidate]: (payload) => {
            const p = payload as { fromConnectionId: string; candidate: RTCIceCandidateInit };
            handlersRef.current.onIceCandidate?.(p.fromConnectionId, p.candidate);
        },
        [MeetingsEvents.MediaToggled]: (payload) => {
            const p = payload as { connectionId: string; mediaType: "audio" | "video"; enabled: boolean };
            handlersRef.current.onMediaToggled?.(p.connectionId, p.mediaType, p.enabled);
        }
    });

    return useMemo<MeetingMeshSignaling>(() => ({
        get isConnected() { return hub.state === HubConnectionState.Connected; },
        joinMeeting: hub.joinMeeting,
        leaveMeeting: hub.leaveMeeting,
        sendOffer: hub.sendOffer,
        sendAnswer: hub.sendAnswer,
        sendIceCandidate: hub.sendIceCandidate,
        toggleMedia: hub.toggleMedia,
        on: (handlers) => {
            handlersRef.current = handlers;
            return () => { handlersRef.current = {}; };
        }
    }), [hub]);
}
